import React, { useEffect, useCallback, useState, useRef } from 'react';
import { Snackbar, Alert, Slide, Button, Box, Typography } from '@mui/material';
import api from '../utils/api';
import { getSoundPreference, setSoundPreference } from '../utils/notificationSounds';

const MAX_VISIBLE = 5;
const MAX_SEEN_IDS = 400;

function SlideDown(props) {
  return <Slide {...props} direction="down" />;
}

function makeToastId() {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
  return `${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;
}

function readSeenIds(storageKey) {
  if (typeof window === 'undefined') return new Set();
  try {
    const raw = localStorage.getItem(storageKey);
    if (!raw) return new Set();
    const arr = JSON.parse(raw);
    return new Set(Array.isArray(arr) ? arr : []);
  } catch {
    return new Set();
  }
}

function writeSeenIds(storageKey, set) {
  if (typeof window === 'undefined') return;
  const arr = [...set];
  const trimmed = arr.length > MAX_SEEN_IDS ? arr.slice(-MAX_SEEN_IDS) : arr;
  localStorage.setItem(storageKey, JSON.stringify(trimmed));
}

/** Stable key for deduping when API omits id */
function notificationDedupeKey(it) {
  if (it?.id) return String(it.id);
  return `${it.created_at || ''}|${it.title || ''}|${it.body || ''}`;
}

function readSoundChoice(prefKey) {
  const v = getSoundPreference(prefKey);
  if (v === true) return 'on';
  if (v === false) return 'off';
  return 'unset';
}

/**
 * Polls notification API, shows stacked toasts once per notification id, optional one-time sound enable prompt.
 */
export default function NotificationStackListener({
  tokenKey,
  sinceKey,
  seenIdsKey,
  soundPrefKey,
  apiPath,
  playSound,
  severity = 'info',
  soundPromptTitle = 'Notification sounds',
  soundPromptBody = 'Play a short sound when new alerts arrive?',
}) {
  const resolvedSeenKey = seenIdsKey || `${sinceKey}_seen_ids`;
  const [toasts, setToasts] = useState([]);
  const pendingRef = useRef([]);
  const [soundChoice, setSoundChoice] = useState(() =>
    typeof window !== 'undefined' ? readSoundChoice(soundPrefKey) : 'unset'
  );
  const [tokenPresent, setTokenPresent] = useState(() =>
    typeof window !== 'undefined' ? !!localStorage.getItem(tokenKey) : false
  );

  const removeToast = useCallback((id) => {
    setToasts((prev) => {
      const next = prev.filter((t) => t.id !== id);
      while (next.length < MAX_VISIBLE && pendingRef.current.length) {
        next.push(pendingRef.current.shift());
      }
      return next;
    });
  }, []);

  const enqueue = useCallback((items) => {
    if (!items?.length) return;
    const entries = items.map((it) => ({
      id: it.id ? String(it.id) : makeToastId(),
      title: it.title || 'Notification',
      body: it.body || '',
    }));
    setToasts((prev) => {
      let combined = [...prev, ...entries];
      if (combined.length <= MAX_VISIBLE) return combined;
      const visible = combined.slice(0, MAX_VISIBLE);
      pendingRef.current.push(...combined.slice(MAX_VISIBLE));
      return visible;
    });
  }, []);

  const poll = useCallback(async () => {
    const hasToken = !!localStorage.getItem(tokenKey);
    setTokenPresent(hasToken);
    if (!hasToken) return;

    try {
      const since =
        localStorage.getItem(sinceKey) || new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString();
      const res = await api.get(apiPath, { params: { since } });
      const items = res.data?.data || [];
      if (items.length === 0) return;

      let maxT = since;
      for (const it of items) {
        const ca = it.created_at;
        if (ca && new Date(ca).getTime() > new Date(maxT).getTime()) maxT = ca;
      }
      const nextSince = new Date(maxT);
      nextSince.setMilliseconds(nextSince.getMilliseconds() + 1);
      localStorage.setItem(sinceKey, nextSince.toISOString());

      const seen = readSeenIds(resolvedSeenKey);
      const fresh = items.filter((it) => {
        const k = notificationDedupeKey(it);
        return k && !seen.has(k);
      });
      for (const it of fresh) {
        seen.add(notificationDedupeKey(it));
      }
      if (fresh.length) {
        writeSeenIds(resolvedSeenKey, seen);
      }

      if (fresh.length === 0) return;

      if (getSoundPreference(soundPrefKey) === true) {
        playSound();
      }

      enqueue(fresh);
    } catch {
      /* ignore */
    }
  }, [tokenKey, sinceKey, resolvedSeenKey, apiPath, soundPrefKey, playSound, enqueue]);

  useEffect(() => {
    const t0 = setTimeout(poll, 2000);
    const id = setInterval(poll, 32000);
    return () => {
      clearTimeout(t0);
      clearInterval(id);
    };
  }, [poll]);

  const handleEnableSound = () => {
    setSoundPreference(soundPrefKey, true);
    setSoundChoice('on');
    playSound();
  };

  const handleDeclineSound = () => {
    setSoundPreference(soundPrefKey, false);
    setSoundChoice('off');
  };

  const showSoundPrompt = soundChoice === 'unset' && tokenPresent;

  return (
    <>
      {toasts.map((t, index) => (
        <Snackbar
          key={t.id}
          open
          autoHideDuration={6000}
          onClose={() => removeToast(t.id)}
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
          TransitionComponent={SlideDown}
          sx={{
            position: 'fixed',
            top: { xs: 56 + index * 108, sm: 64 + index * 108 },
            left: '50%',
            right: 'auto',
            transform: 'translateX(-50%)',
            maxWidth: 'calc(100% - 24px)',
            zIndex: (theme) => theme.zIndex.snackbar + index,
          }}
        >
          <Alert
            severity={severity}
            variant="filled"
            onClose={() => removeToast(t.id)}
            sx={{
              width: '100%',
              minWidth: { xs: 280, sm: 400 },
              maxWidth: 520,
              boxShadow: 4,
              borderRadius: 2,
              '& .MuiAlert-message': { width: '100%' },
            }}
          >
            <strong>{t.title}</strong>
            {t.body ? (
              <Box component="div" sx={{ mt: 1, fontWeight: 400, opacity: 0.95 }}>
                {t.body}
              </Box>
            ) : null}
          </Alert>
        </Snackbar>
      ))}

      <Snackbar
        open={showSoundPrompt}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        sx={{ mb: { xs: 2, sm: 3 }, zIndex: (theme) => theme.zIndex.snackbar + MAX_VISIBLE + 2 }}
      >
        <Alert
          severity="info"
          variant="outlined"
          sx={{
            alignItems: 'flex-start',
            maxWidth: 420,
            boxShadow: 3,
            bgcolor: 'background.paper',
          }}
          action={
            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 1, mt: { xs: 1, sm: 0 } }}>
              <Button size="small" color="inherit" onClick={handleDeclineSound}>
                Not now
              </Button>
              <Button size="small" variant="contained" onClick={handleEnableSound}>
                Enable sounds
              </Button>
            </Box>
          }
        >
          <Box>
            <Typography variant="subtitle2" fontWeight={600}>
              {soundPromptTitle}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              {soundPromptBody}
            </Typography>
          </Box>
        </Alert>
      </Snackbar>
    </>
  );
}
